import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { catchError, EMPTY, finalize, of, switchMap, tap, Observable, Subscription, map } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import {
  CreateTemplateVersionResponse,
  Template,
  TemplateRequest,
  UploadUrlResponse
} from '@workspaces/models/templates';
import { WorkspaceTemplateService } from '@workspaces/services/templates.service';
import { CommonModule } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state/app.state';
import { templateFeature, TemplateFetch } from '@workspaces/store/template.feature';
import { TemplateListElementComponent } from '../template-list/template-list-element/template-list-element.component';
import { TemplateCardComponent } from '../template-list/template-card/template-card.component';
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelDescription } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TemplateFormComponent } from '../template-form/template-form.component';

@Component({
  selector: 'app-template-edit',
  templateUrl: './template-edit.component.html',
  styleUrls: ['./template-edit.component.scss', '../../global-workspace-styles.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatTabsModule,
    RouterModule,
    MatButtonModule,
    MatSnackBarModule,
    TemplateListElementComponent,
    TemplateCardComponent,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatIcon,
    MatExpansionPanelDescription,
    MatProgressSpinner,
    TemplateFormComponent
  ]
})
export class TemplateEditComponent implements OnDestroy, OnInit {
  @ViewChild(TemplateFormComponent) templateForm!: TemplateFormComponent;
  private readonly sub = new Subscription();
  public loading = false;
  public isExpansionPanelOpen = false;
  public isLoading$!: Observable<boolean>;
  public templatesData$!: Observable<Template[]>;
  public isListView = false;

  private templateId: string | null = null;
  constructor(
    private readonly templateService: WorkspaceTemplateService,
    private readonly http: HttpClient,
    private readonly snackBar: MatSnackBar,
    private readonly route: ActivatedRoute,
    private readonly store: Store<AppState>
  ) {}
  ngOnInit(): void {
    this.store.dispatch(TemplateFetch({ show_latest: false }));
    this.templateId = this.route.snapshot.paramMap.get('templateId');
    this.templatesData$ = this.store.select(templateFeature.selectData).pipe(
      map(templates => {
        return templates.filter(t => t.id === this.templateId).reverse();
      })
    );

    this.isLoading$ = this.store.select(templateFeature.templatesIsLoading);
  }

  onSubmit(event: { form: TemplateRequest; file: File | null }): void {
    this.loading = true;

    const process$ = this.createTemplateVersion(event.form).pipe(
      switchMap(template => this.handleOptionalFileUpload(template, event.file)),
      tap(() => this.onSuccess()),
      catchError(() => this.handleError()),
      finalize(() => {
        this.loading = false;
        this.store.dispatch(TemplateFetch({ show_latest: false }));
        this.isExpansionPanelOpen = false;
      })
    );

    this.sub.add(process$.subscribe());
  }

  private createTemplateVersion(payload: TemplateRequest): Observable<CreateTemplateVersionResponse> {
    if (!this.templateId) {
      return EMPTY;
    }
    return this.templateService.createTemplateVersion(this.templateId, payload);
  }

  private handleOptionalFileUpload(template: CreateTemplateVersionResponse, file: File | null) {
    if (!file) {
      return of(template);
    }

    const extension = file.name.split('.').pop() ?? '';
    return this.templateService
      .generateUploadUrl(template.id, template.version, extension)
      .pipe(switchMap((uploadRes: UploadUrlResponse) => this.uploadToS3(uploadRes.url, file)));
  }

  private uploadToS3(url: string, file: File) {
    const headers = new HttpHeaders({ 'Content-Type': file.type });
    return this.http.put(url, file, { headers });
  }

  private onSuccess(): void {
    this.snackBar.open('Template created successfully!', 'Dismiss', { duration: 3000 });
    this.templateForm.reset();
  }

  private handleError(): Observable<never> {
    this.snackBar.open('Error during template creation', 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
    return EMPTY;
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
